import { BLOCKS } from '@contentful/rich-text-types';
import { graphql } from 'gatsby';
import get from 'lodash.get';
import React, { useEffect } from 'react';
import StickyBox from 'react-sticky-box';

import PageSection from '../components/Common/PageSection';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import SmartLink from '../components/Common/SmartLink';
import Accordion from '../components/Generic/Accordion';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import * as styles from './legal.module.css';

const Legal = ({ data: { page, template }, location }) => {
  const Menu = () => {
    let menu = [
      <li key={'home'}>
        <SmartLink to="/legal">Legal Home</SmartLink>
      </li>,
    ];
    template.nodes
      .filter((item) => item.slug.indexOf('legal/') === 0)
      .sort((a, b) => {
        if (a.pageTitle < b.pageTitle) {
          return -1;
        }
        if (a.pageTitle > b.pageTitle) {
          return 1;
        }
        return 0;
      })
      .forEach((item, i) => {
        menu.push(
          <li key={i}>
            <SmartLink to={`/${item.slug}`}>{item.pageTitle}</SmartLink>
          </li>
        );
      });
    return menu;
  };

  let tableHeaders = [];
  let tableHeadersBackup = [];
  useEffect(() => {
    let tableCell = document.querySelectorAll('.rich-text-table-cell');
    tableCell.forEach((element) => {
      if (tableHeaders.length !== 0) {
        element.setAttribute('data-label', tableHeaders.shift());
      } else {
        tableHeaders.push(...tableHeadersBackup);
        element.setAttribute('data-label', tableHeaders.shift());
      }
    });
  });

  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = get(node, 'data.target.internal.type');

        if (type === 'ContentfulJumpLink') {
          return <div id={get(node, 'data.target.sectionId')} />;
        }
        if (type === 'ContentfulGroupedContent') {
          const groupType = get(node, 'data.target.type');

          if (groupType === 'Accordion') {
            const content = get(node, 'data.target.content');
            const faqItems = content.map((faq) => {
              return {
                title: faq.title,
                supportingText: faq.body,
              };
            });
            return <Accordion items={faqItems} className={styles.faqs} defaultCollapsed />;
          }
        }
      },

      [BLOCKS.TABLE]: (node, children) => (
        <table style={{ marginBottom: '2rem' }} role="table" className="rich-text-table">
          {children}
        </table>
      ),

      [BLOCKS.TABLE_ROW]: (node, children) => {
        node.content.forEach((element) => {
          if (element.nodeType === 'table-header-cell') {
            tableHeaders.push(element.content[0].content[0].value);
            tableHeadersBackup.push(element.content[0].content[0].value);
          }
        });
        return (
          <tr role="row" className="rich-text-row">
            {children}
          </tr>
        );
      },

      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th className="rich-text-columnheader" role="columnheader">
          {children}
        </th>
      ),

      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td role="cell" className="rich-text-table-cell">
          {children}
        </td>
      ),
    },
  };

  return (
    <Layout background={'grey'} className={styles.legalOverrides} pageMeta={page.pageMetaData}>
      <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
      <PageSection isTopSection>
        <div className={styles.layout}>
          <div className={styles.sticky}>
            <StickyBox offsetTop={84} offsetBottom={10}>
              <nav className={styles.menu}>
                <ul className={'unstyled-list'}>
                  <Menu />
                </ul>
              </nav>
            </StickyBox>
          </div>
          <section>
            <RichTextConverter copy={get(page, 'body')} options={options} />
          </section>
        </div>
      </PageSection>
    </Layout>
  );
};

export default Legal;

export const query = graphql`
  query LegalTemplateQuery($slug: String!) {
    template: allContentfulRichTextPageTemplate {
      nodes {
        pageTitle
        slug
      }
    }
    page: contentfulRichTextPageTemplate(slug: { eq: $slug }) {
      body {
        raw
        references {
          ... on Node {
            ...JumpLink
          }
          ... on Node {
            ... on ContentfulGroupedContent {
              contentful_id
              type
              content {
                ... on ContentfulSimpleCard {
                  title
                  body {
                    raw
                  }
                }
              }
              internal {
                type
              }
            }
          }
        }
      }
      pageMetaData {
        ... on Node {
          ...PageMetaData
        }
      }
    }
  }
`;
